import { useLocation, NavLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../api/axios';
import { formatMoney } from '../../utils/helpers';

const SESSION_KEY = 'bom_thankyou_state';

const ThankYouPage = () => {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const emailSentRef = useRef(false);

  // Persist state to sessionStorage so a page refresh doesn't lose the data.
  // On first load (from registration flow), location.state has the data — save it.
  // On refresh, location.state is null — restore from sessionStorage.
  let pageState = location.state;
  if (pageState && (pageState.event || pageState.attendee)) {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(pageState)); } catch (_) {}
  } else {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) pageState = JSON.parse(stored);
    } catch (_) {}
  }

  //added isAddition to control the message
  const {
    event,
    attendee,
    attendees,
    registration,
    order: stateOrder,
    paymentCompleted,
    payLater,
    isAddition
  } = pageState || {};

  // Holds the live order — starts with what was passed through state,
  // gets overwritten once the API fetch resolves with the current balance.
  const [order, setOrder] = useState(stateOrder);
  const [user, setUser] = useState({});

  const emailSessionKey = attendee?.BPAttendeeID
    ? `thankyou_email_sent_${attendee.BPAttendeeID}`
    : null;

  const buildAttendeeListHtml = (attendeesData) => {
    if (!attendeesData) return '';

    const attendeeArray = Array.isArray(attendeesData)
      ? attendeesData
      : [attendeesData];

    return `
      <ul style="margin:12px 0;padding-left:20px;">
        ${attendeeArray
          .map(
            (a) => `
              <li style="margin-bottom:8px;">
                <strong>${a?.BPAttendeeFN || ''} ${a?.BPAttendeeLN || ''}</strong>
                ${a?.BPAttendeeType ? ` (${a.BPAttendeeType})` : ''}
                <br />
                ${a?.BPAttendeeEmail || ''}
              </li>
            `
          )
          .join('')}
      </ul>
    `;
  };

  const buildThankYouHtml = (
    templateHtml,
    event,
    attendee,
    attendees,
    registration
  ) => {
    if (!templateHtml) return null;

    const attendeeListHtml = buildAttendeeListHtml(
      attendees?.length ? attendees : attendee
    );

    const replacements = {
      "{%EventID%}": event?.EventsID ?? "",
      "{%EventName%}": event?.BP_Title ?? "",
      "{%Type%}": registration?.RegistrationName ?? "",
      "{% Domain %}": window.location.origin,
      "{%Domain%}": window.location.origin,
      "{%AttendeeName%}":
        `${attendee?.BPAttendeeFN ?? ""} ${attendee?.BPAttendeeLN ?? ""}`.trim(),
      "{%AttendeeEmail%}": attendee?.BPAttendeeEmail ?? "",
      "{%EventDate%}": event?.BP_StartDate ?? "",
      "{%EventLocation%}": event?.BP_Address ?? "",
      "{%Attendees%}": attendeeListHtml,
    };

    return Object.entries(replacements).reduce(
      (html, [placeholder, value]) =>
        html.replaceAll(placeholder, String(value)),
      templateHtml
    );
  };

  useEffect(() => {
    if (!attendee?.BPAttendeeID || !event?.EventsID) return;

    const sendEmail = async () => {
      try {
        const resp = await axios.post(`/api/events/${event.EventsID}/send-thankyou-email`, {
          attendeeId: attendee.BPAttendeeID,
          isAddition: !!isAddition,
        });
        if (resp.data?.skipped) {
        }
      } catch (e) {
        console.error('Email send failed:', e);
        enqueueSnackbar('Failed to send confirmation email', { variant: 'error' });
      }
    };

    sendEmail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: fire once on mount, server handles dedup

  useEffect(() => {
    if (!stateOrder?.OrderID) return;

    const fetchOrder = async () => {
      try {
        const orderRes = await axios.get(`/api/orders/${stateOrder.OrderID}`);
        if (orderRes?.data) {
          setOrder(orderRes.data.order);
        }
      } catch (e) {
        console.error('Failed to fetch latest order:', e);
        // Fall back silently — stateOrder is still in place
      }
    };

    fetchOrder();
  }, [stateOrder?.OrderID]);

  useEffect(() => {
    if (!attendee) return;

    const fetchUser = async () => {
      try {
        const userRes = await axios.get(`/api/users/${attendee.BPAttendeeUserID}`);
        if (userRes?.data) {
          setUser(userRes.data);
        }
      } catch (e) {
        console.error('Failed to fetch user:', e);
        // Fall back silently — name is just omitted from the greeting
      }
    };

    fetchUser();
  }, [attendee]);

  if (!event || !attendee) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12">
                <h1 className="text-uppercase">Registration</h1>
                <p style={{ margin: 0 }}>Confirmation</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid py-5 text-center">
            <div style={{
              maxWidth: 480,
              margin: '0 auto',
              padding: '2.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              background: '#fff',
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <h5 style={{ fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>No confirmation data found</h5>
              <p style={{ color: '#6b7280', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                This page is only accessible after completing a registration. If you just registered, try refreshing — or navigate back to find your event.
              </p>
              <NavLink
                to="/events"
                style={{
                  backgroundColor: '#003063',
                  color: '#fff',
                  padding: '0.6rem 1.5rem',
                  borderRadius: '0.375rem',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Find Events
              </NavLink>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const isPending = registration?.RequireApproval;
  const message = isPending
    ? event?.BP_PendingApprovalMessage
    : isAddition
      ? (event?.BP_AdditionalMessage || event?.BP_ThankYouMessage)
      : event?.BP_ThankYouMessage;

  const html = buildThankYouHtml(
    message,
    event,
    attendee,
    attendees,
    registration
  );

  // Only used when the event has no message configured for this scenario.
  const fallbackText = isPending
    ? 'A coordinator will review your submission and notify you once a decision has been made.'
    : !isAddition
      ? 'Your registration has been confirmed. A confirmation email has been sent to your inbox.'
      : 'The additional member(s) have been added to your existing registration. A confirmation email has been sent to your inbox.';

  const balance = parseFloat(order?.Balance || 0);
  const hasBalance = order && balance > 0;
  const showMakePaymentLink = hasBalance && !paymentCompleted && !payLater && !isPending;
  const attendeeName = `${user?.FirstName ?? ''} ${user?.LastName ?? ''}`.trim();

  return (
    <main>
      {/* Page Header */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">
                {isPending ? 'Registration Pending' : 'Registration Complete'}
              </h1>
              <p style={{ margin: 0 }}>{event?.BP_Title || 'Thank you for registering'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="full-row white">
        <div className="container-fluid py-4">

          {/* Status Card */}
          <style>{`.thankYouStatusBox a { color: #c74634 !important; }`}</style>
          <div className="thankYouStatusBox" style={{
            border: `1px solid ${isPending ? '#f59e0b' : '#16a34a'}`,
            borderRadius: '0.75rem',
            background: isPending ? '#fffbeb' : '#f0fdf4',
            padding: '2rem',
            marginBottom: '1.5rem',
            display: 'flex',
            gap: '1.25rem',
            alignItems: 'flex-start',
          }}>
            {/* Icon */}
            <div style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: isPending ? '#f59e0b' : '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {isPending ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>

            {/* Message */}
            <div style={{ flex: 1 }}>
              <h4 style={{ fontWeight: 700, color: '#212540', marginBottom: '0.35rem' }}>
                {isPending
                  ? 'Your registration is pending approval'
                  : !isAddition
                    ? `Thanks for Registering${attendeeName ? `, ${attendeeName}` : ''}!`
                    : `Your team has been updated successfully${attendeeName ? `, ${attendeeName}` : ''}`}
              </h4>

              {/* Payment completed / pay later banners */}
              {paymentCompleted && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#ffffff',
                  border: `1px solid ${isPending ? '#f59e0b' : '#16a34a'}`,
                  borderRadius: '0.5rem',
                  color: '#212540',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}>
                  &#10003;&nbsp; Payment has been processed successfully.
                </div>
              )}

              {payLater && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#ffffff',
                  border: `1px solid ${isPending ? '#f59e0b' : '#16a34a'}`,
                  borderRadius: '0.5rem',
                  color: '#212540',
                  fontSize: '0.9rem',
                }}>
                  You can complete your payment later from{' '}
                  <NavLink to="/myreserved" style={{ color: '#c74634', fontWeight: 600 }}>My Reservations &amp; Payments</NavLink>,
                  or by mail or phone.
                </div>
              )}

              {/* Event message (pending / additional / thank-you, per event setup) */}
              {html ? (
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: `1px solid ${isPending ? '#fcd34d' : '#86efac'}`,
                    fontSize: '0.95rem',
                    color: '#212540',
                    lineHeight: 1.65,
                  }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <p style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: `1px solid ${isPending ? '#fcd34d' : '#86efac'}`,
                  color: '#212540',
                  fontSize: '0.95rem',
                  marginBottom: 0,
                }}>
                  {fallbackText}
                </p>
              )}
            </div>
          </div>

          {/* Order Details Card */}
          {order && (
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              background: '#ffffff',
              overflow: 'hidden',
              marginBottom: '1.5rem',
            }}>
              {/* Card Header */}
              <div style={{
                background: '#f8f9fa',
                borderBottom: '1px solid #e5e7eb',
                padding: '1rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#003063" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span style={{ fontWeight: 600, color: '#21252C', fontSize: '1rem' }}>Order Details</span>
              </div>

              {/* Order Rows */}
              <div style={{ padding: '0 1.5rem' }}>
                {[
                  { label: 'Order ID', value: `#${order.OrderID}` },
                  { label: 'Product', value: order.Products },
                  { label: 'Date', value: order.OrderDate ? new Date(order.OrderDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—' },
                  { label: 'Balance Due', value: formatMoney(balance), highlight: hasBalance },
                ].map(({ label, value, highlight }, i, arr) => (
                  <div key={label} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.85rem 0',
                    borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                  }}>
                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{label}</span>
                    <span style={{
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: highlight ? '#dc2626' : '#111827',
                    }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Make Payment Button */}
              {showMakePaymentLink && (
                <div style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  background: '#fafafa',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'center',
                }}>
                  <NavLink
                    to={`/makepayment/${attendee.BPAttendeeID}`}
                    style={{
                      backgroundColor: '#003063',
                      color: '#ffffff',
                      padding: '0.6rem 1.5rem',
                      borderRadius: '0.375rem',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Make Payment
                  </NavLink>
                  <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                    Balance of <strong style={{ color: '#dc2626' }}>{formatMoney(balance)}</strong> is due
                  </span>
                </div>
              )}

              {!hasBalance && (
                <div style={{
                  padding: '0.85rem 1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  background: '#f0fdf4',
                  color: '#16a34a',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}>
                  &#10003;&nbsp; Paid in full
                </div>
              )}
            </div>
          )}

          {/* Navigation Links */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <NavLink
              to="/myreserved"
              style={{
                backgroundColor: '#003063',
                color: '#ffffff',
                padding: '0.6rem 1.5rem',
                borderRadius: '0.375rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              My Reservations
            </NavLink>
            <NavLink
              to="/events"
              style={{
                backgroundColor: '#ffffff',
                color: '#003063',
                padding: '0.6rem 1.5rem',
                borderRadius: '0.375rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                border: '1px solid #003063',
              }}
            >
              Find More Events
            </NavLink>
          </div>

        </div>
      </div>
    </main>
  );
};

export default ThankYouPage;